import { UserTask, UserTaskStatus, UserTaskType } from "./UserTask";

export enum UserTaskStepStatus {
  BLOCKED = "BLOCKED",
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  WAITING = "WAITING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  SKIPPED = "SKIPPED",
  CANCELLED = "CANCELLED",
}

export enum UserTaskActor {
  CLIENT = "CLIENT",
  AGENT = "AGENT",
  SYSTEM = "SYSTEM",
  EXTERNAL = "EXTERNAL",
}

// step_code values referenced by name from UI code (not just displayed via
// formatStepCode). Mirrors backend PortalStepCode
// (backend/Services/user_task/workflow/step_codes.py) plus other steps UI code
// needs to check by name - step_code itself stays a free-form string across the
// workflow engine (each WorkflowDefinition defines its own codes), this just gives
// type-safe references at the specific call sites that need one.
export const UserTaskStepCode = {
  CLIENT_SIGNATURE: "CLIENT_SIGNATURE",
  SHOW_BANK_DETAILS: "SHOW_BANK_DETAILS",
  UPLOAD_TRANSFER_CONFIRMATION: "UPLOAD_TRANSFER_CONFIRMATION",
  UPLOAD_ID_DOCUMENT: "UPLOAD_ID_DOCUMENT",
  // the client signs the producer's form in the system (forms engine) - the signed PDF is the step's document
  REQUEST_FORM_SIGNATURE: "REQUEST_FORM_SIGNATURE",
  TRANSFER_FORM_SIGNATURE: "TRANSFER_FORM_SIGNATURE",
  // the task's documents emailed to the producer (SendTaskDocumentsDialog)
  SEND_TO_COMPANY: "SEND_TO_COMPANY",
} as const;

export interface UserTaskStep {
  id: number;
  company_id: number;
  task_id: number;
  step_code: string;
  step_order: number;
  actor: UserTaskActor;
  status: UserTaskStepStatus;
  is_required: boolean;
  is_automatic: boolean;
  is_blocking: boolean;
  is_customer_facing?: boolean;
  started_at?: string;
  completed_at?: string;
  completed_by_user_id?: number;
  completed_by_contact_id?: number;
  failure_reason?: string;
  result_data?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface UserTaskStepSummary {
  step_code: string;
  status: UserTaskStepStatus;
  step_order: number;
}

export interface UserTaskWithWorkflow extends UserTask {
  current_step_code?: string;
  current_step_status?: UserTaskStepStatus;
  current_step_actor?: UserTaskActor;
  workflow_progress_completed: number;
  workflow_progress_total: number;
  steps_summary?: UserTaskStepSummary[];
}

export interface UserTaskDetails {
  id: number;
  company_id: number;
  task: UserTask;
  steps: UserTaskStep[];
  workflow_progress_completed: number;
  workflow_progress_total: number;
}

export enum ManualStepUpdateAction {
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  SKIPPED = "SKIPPED",
}

export interface ManualStepUpdateRequest {
  action: ManualStepUpdateAction;
  reason: string;
  notes?: string;
}

export interface CreateWorkflowTaskRequest {
  task_type: UserTaskType;
  contact_id?: number;
  created_by?: number;
  title?: string;
  description?: string;
  assigned_to_id?: number;
  board_id?: number;
  list_id?: number;
  policy_number?: string;
  product_type?: number;
  adjusted_product_type?: number;
  handler_code?: string;
  reported_period?: string;
  simulation_id?: number;
  simulation_mimshak_data_id?: number;
  selected_step_codes?: string[];
}

export interface WorkflowStepDefinition {
  code: string;
  order: number;
  actor: UserTaskActor;
  is_required: boolean;
  is_automatic: boolean;
  is_blocking: boolean;
  is_customer_facing?: boolean;
}

export interface WorkflowDefinition {
  code: string;
  task_type: UserTaskType;
  steps: WorkflowStepDefinition[];
}

export interface VerifyTasksRequest {
  reported_period: string;
}

export interface VerifyTasksSummary {
  checked: number;
  matched: number;
  mismatched: number;
  pending: number;
  awaiting_reflection: number;
  failed: number;
}

// ---------- Forms preparation (עזרה בהכנת טפסים) ----------

export interface FormsPreparationPath {
  path_code: number;
  path_name?: string | null;
  amount?: number | null;
  deposit_percentage?: number | null;
}

export interface FormsPreparationTransferSource {
  handler_code?: string | null;
  policy_number?: string | null;
  product_type?: number | null;
  amount?: number | null;
}

/** a beneficiary to put in the forms (a beneficiaries update) */
export interface FormsPreparationBeneficiary {
  first_name?: string | null;
  last_name?: string | null;
  id_number?: string | null;
  relation_type?: number | null;
  percentage?: number | null;
}

/** a task whose forms (in the external forms system) are not prepared yet - with what is needed to fill them */
export interface FormsPreparationItem {
  task_id: number;
  contact_id?: number | null;
  simulation_id?: number | null;
  task_type: UserTaskType;
  change_types: string[];
  title?: string | null;
  handler_code?: string | null;
  product_type?: number | null;
  /** null - a new fund (no policy number yet) */
  policy_number?: string | null;
  is_new_fund: boolean;
  funds_sum?: number | null;
  transfer_in?: number | null;
  deposit_amount?: number | null;
  recurrent_deposit?: number | null;
  standing_order_day?: number | null; // read from the row's deposit_collection_details - not stored
  withdrawal_amount?: number | null;
  saving_fee?: number | null;
  deposit_fee?: number | null;
  paths: FormsPreparationPath[];
  transfer_sources: FormsPreparationTransferSource[];
  /** a beneficiaries update - the beneficiaries the fund should have */
  beneficiaries: FormsPreparationBeneficiary[];
  /** the documents check (the step before) is done - only then can the forms step be completed */
  documents_checked: boolean;
  prepare_step_status: UserTaskStepStatus;
}
